import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Spinner } from 'react-bootstrap';
import FormPage from '../components/FormPage';
import FormCard from '../components/FormCard';
import FormField from '../components/FormField';
import FormActions from '../components/FormActions';
import { telegramUserService } from '../services/telegramUserService';
import { useTranslation } from '../contexts/LocaleContext';
import { ROUTES } from '../constants';

const TelegramUserForm: React.FC = () => {
  const t = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [chatId, setChatId] = useState<number>(0);
  const [loading, setLoading] = useState(isEdit);

  useEffect(() => {
    if (id) loadUser();
  }, [id]);

  const loadUser = async () => {
    if (!id) return;
    try {
      const user = await telegramUserService.get(parseInt(id, 10));
      setChatId(user.chatId);
    } catch (error) {
      console.error('Error loading telegram user:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isEdit && !id) return;
    if (!isEdit && !chatId) {
      alert(t('formField.telegramUserIdPlaceholder'));
      return;
    }
    try {
      if (isEdit) {
        await telegramUserService.update(parseInt(id!, 10), { chatId });
      } else {
        await telegramUserService.create({ chatId });
      }
      navigate(ROUTES.TELEGRAM_ACCESS);
    } catch (error) {
      console.error(isEdit ? 'Error updating telegram user' : 'Error creating telegram user', error);
      alert(isEdit ? t('errors.updateTelegramUser') : t('errors.createTelegramUser'));
    }
  };

  if (isEdit && loading) {
    return (
      <FormPage title={t('telegramAccess')} backFallbackRoute={ROUTES.TELEGRAM_ACCESS}>
        <div className="d-flex justify-content-center my-4">
          <Spinner animation="border" role="status" />
        </div>
      </FormPage>
    );
  }

  return (
    <FormPage
      title={
        isEdit
          ? `${t('manage.edit')} — ${t('telegramAccess')}`
          : t('giveTelegramAccess')
      }
      backFallbackRoute={ROUTES.TELEGRAM_ACCESS}
    >
      <Form onSubmit={handleSubmit}>
        <FormCard>
          <FormField
            label={t('formField.telegramUserId')}
            htmlFor="chatId"
            helper={t('helpers.telegram.userId')}
          >
            <Form.Control
              type="number"
              id="chatId"
              value={chatId || ''}
              onChange={(e) => setChatId(parseInt(e.target.value, 10) || 0)}
              placeholder={t('formField.telegramUserIdPlaceholder')}
              required
            />
          </FormField>
          <FormActions
            submitLabel={t('manage.save')}
            backTo={ROUTES.TELEGRAM_ACCESS}
            backLabel={t('telegramAccess')}
          />
        </FormCard>
      </Form>
    </FormPage>
  );
};

export default TelegramUserForm;
